/**
 * @file DeviceChannelOpener.ts
 * @description 根据语义化通道描述打开同一物理设备上的辅助 HID 通道。
 * 这里集中处理 WebHID 已授权设备查找，避免 BaseDevice 或 Feature 直接按 usage 扫描设备。
 */

import type { ChannelDescriptor } from '../../channels/Channel'
import { HidChannelFactory } from '../../channels/hid/HidChannelFactory'
import type { HidChannel } from '../../channels/hid/HidChannel'
import { WebHidChannel } from '../../channels/hid/webhid/WebHidChannel'
import type { BaseDevice } from '../BaseDevice'
import { DeviceFactory } from '../DeviceFactory'

export type OpenChannelOptions = {
  /**
   * 已授权列表没有匹配项时是否调用 requestDevice 弹窗。
   * 默认 false：仅 GCOME 等明确需要用户授权 Boot 口的流程传 true。
   * 进 Boot 后的异步重试阶段必须为 false（已无用户手势，Chrome 不会弹窗）。
   */
  requestIfMissing?: boolean
}

/**
 * 为当前设备创建一个指定用途的临时通道设备。
 *
 * 注意：这里目前只覆盖 WebHID，因为浏览器授权模型需要从已授权设备列表中再次选出目标 interface。
 * NodeHID/BLE 后续应在这里按同样的 ChannelDescriptor 契约补实现，而不是散落到各个 Feature。
 */
export async function createDeviceForChannelDescriptor(
  baseDevice: BaseDevice,
  descriptor?: ChannelDescriptor | null,
  options?: OpenChannelOptions,
): Promise<BaseDevice | null> {
  const channel = await createHidChannelForDescriptor(baseDevice, descriptor, options)
  if (!channel) return null

  const device = DeviceFactory.create(baseDevice.manifest, channel)
  // open() 会根据主通道语义决定是否执行设备业务初始化；辅助设备必须在
  // open 之前继承 descriptor 的真实 usageType，避免升级口误跑配置/DPI 读取。
  device.primaryUsageType = descriptor?.usageType ?? 'default'
  // 关键：辅助通道设备与主设备属于同一会话，必须继承相同的固件运行时配置。
  device.__setFirmwareBaseUrl(baseDevice.getFirmwareBaseUrl())
  device.__setFirmwareVersionPolicy(baseDevice.getFirmwareVersionPolicy())
  try {
    await device.open()
  } catch (error) {
    // 关键：部分升级接口只需要原始通道可用，驱动初始化读写失败不能直接阻断升级入口。
    console.warn('[DeviceChannelOpener] Device init warning for sibling channel', error)
  }

  return device
}

/**
 * 根据通道描述创建并打开辅助 HID channel。
 *
 * 匹配条件（usage/usagePage 来自 manifest channels）：
 * 1. 通道身份（filter 可覆盖独立 Boot VID/PID）+ 顶层 collection 的 usagePage+usage
 * 2. 优先与主设备相同 productId 的候选，避免鼠标/接收器双授权时误绑另一物理设备
 * 3. 同 PID 内优先非主控制口 native，但允许复用主 native（VT2 接收器 MI_03 同时包含 FF1C:92 与 FFEF:00）
 * 4. 无同 PID 命中时：仅当 filter 显式覆盖独立 VID/PID（Boot 重枚举）才允许跨 PID；否则拒绝
 * 5. 仍无命中且显式 requestIfMissing=true：requestDevice 弹窗（须用户手势），结果仍按上述规则精确匹配
 */
export async function createHidChannelForDescriptor(
  baseDevice: BaseDevice,
  descriptor?: ChannelDescriptor | null,
  options?: OpenChannelOptions,
): Promise<HidChannel | null> {
  if (!descriptor || descriptor.usagePage === undefined || descriptor.usage === undefined) {
    console.warn('[DeviceChannelOpener] Channel descriptor missing usagePage/usage')
    return null
  }

  if (!WebHidChannel.isSupported()) {
    console.warn('[DeviceChannelOpener] Creating sibling channel currently requires WebHID')
    return null
  }

  // opt-in：默认不弹窗，避免 GCOME 授权逻辑污染其他设备的升级/配对通道打开
  const requestIfMissing = options?.requestIfMissing === true
  const identity = resolveChannelIdentity(baseDevice, descriptor)
  const devices = await WebHidChannel.getConnected()
  const primaryNative = baseDevice.channel.nativeDevice
  const pickExactMatch = (candidates: HIDDevice[]): HIDDevice | undefined =>
    pickSiblingHidDevice(candidates, primaryNative, descriptor)

  let target = pickExactMatch(
    devices.filter((device) => matchesUsagePageAndUsage(device, descriptor, identity)),
  )

  // 仅调用方显式要求时才弹窗（GCOME Boot 授权等）；其他设备匹配失败直接返回 null
  if (!target && requestIfMissing) {
    const filters = [
      ...identity.productIds.map((productId) => ({
        vendorId: identity.vendorId,
        productId,
        usagePage: descriptor.usagePage,
        usage: descriptor.usage,
      })),
      ...identity.productIds.map((productId) => ({
        vendorId: identity.vendorId,
        productId,
      })),
    ]
    const requested = await WebHidChannel.scan(filters)
    target = pickExactMatch(
      requested.filter((device) => matchesUsagePageAndUsage(device, descriptor, identity)),
    )
  }

  if (!target) {
    const wanted = `usageType=${descriptor.usageType} VID=0x${identity.vendorId.toString(
      16,
    )} PID=[${identity.productIds.map((p) => `0x${p.toString(16)}`).join(',')}] usagePage=0x${Number(
      descriptor.usagePage,
    ).toString(16)} usage=0x${Number(descriptor.usage).toString(16)}`
    const seen = devices
      .map((device) => {
        const cols = (device.collections ?? [])
          .map((c) => `0x${Number(c.usagePage).toString(16)}:0x${Number(c.usage).toString(16)}`)
          .join(',')
        return `VID=0x${device.vendorId.toString(16)} PID=0x${device.productId.toString(16)} cols=[${cols || 'empty'}]`
      })
      .join(' | ')
    console.warn(
      `[DeviceChannelOpener] No authorized HID device matched ${wanted}. requestIfMissing=${requestIfMissing} authorized=${devices.length} ${seen || '(none)'}`,
    )
    return null
  }

  const channel = HidChannelFactory.create(baseDevice.manifest.hardware.transport, target)
  try {
    await channel.open()
  } catch (error) {
    console.error('[DeviceChannelOpener] Failed to open channel', error)
    return null
  }

  return channel
}

/** 解析通道身份：filter 可覆盖 Bootloader 等独立 VID/PID。 */
function resolveChannelIdentity(
  baseDevice: BaseDevice,
  descriptor: ChannelDescriptor,
): { vendorId: number; productIds: number[] } {
  const vendorId = descriptor.vendorId ?? baseDevice.manifest.hardware.vendorId
  const rawProductId = descriptor.productId ?? baseDevice.manifest.hardware.productId
  const productIds = Array.isArray(rawProductId) ? rawProductId : [rawProductId]
  return { vendorId, productIds }
}

/**
 * 从已按 usage 过滤的候选中选出辅助口。
 *
 * 同 model 多 PID（有线鼠标 + 无线接收器）同时授权时，必须优先绑定当前主设备 PID，
 * 否则会把鼠标升级命令发到接收器（或反过来）。独立 Boot 身份由 filter 显式覆盖 VID/PID 时除外。
 */
export function pickSiblingHidDevice(
  candidates: HIDDevice[],
  primaryNative: unknown,
  descriptor: ChannelDescriptor,
): HIDDevice | undefined {
  if (!candidates.length) return undefined

  const primaryPid = readHidProductId(primaryNative)
  const samePidCandidates =
    primaryPid !== undefined
      ? candidates.filter((candidate) => candidate.productId === primaryPid)
      : []

  const identityRemapped = descriptor.vendorId !== undefined || descriptor.productId !== undefined

  const pool =
    samePidCandidates.length > 0
      ? samePidCandidates
      : identityRemapped || primaryPid === undefined
        ? candidates
        : []

  if (!pool.length) return undefined
  return pool.find((candidate) => candidate !== primaryNative) ?? pool[0]
}

function readHidProductId(native: unknown): number | undefined {
  if (!native || typeof native !== 'object' || !('productId' in native)) return undefined
  const productId = (native as { productId?: unknown }).productId
  return typeof productId === 'number' ? productId : undefined
}

function matchesIdentity(
  device: HIDDevice,
  identity: { vendorId: number; productIds: number[] },
): boolean {
  return device.vendorId === identity.vendorId && identity.productIds.includes(device.productId)
}

function matchesUsagePageAndUsage(
  device: HIDDevice,
  descriptor: ChannelDescriptor,
  identity: { vendorId: number; productIds: number[] },
): boolean {
  if (!matchesIdentity(device, identity)) return false
  const cols = device.collections ?? []
  // 未 open 时 collections 常为空；仅当 filter 显式声明了独立 VID 时按 VID/PID 收下。
  if (!cols.length) {
    return descriptor.vendorId !== undefined
  }
  return (
    cols.some(
      (collection) =>
        collection.usagePage === descriptor.usagePage && collection.usage === descriptor.usage,
    ) ?? false
  )
}
